(() => {
  const config = {
    enabled: false,
    productHandle: "sea-salt-caramel-popcorn",
    desiredQuantity: 1,
    onlyWhenCartHasItems: true,
    removeWhenDisabled: false,
    debug: true
  };

  let isRunning = false;
  let lastAutoActionAt = 0;

  const log = (...args) => {
    if (!config.debug) return;
    // eslint-disable-next-line no-console
    console.log('[auto-add]', ...args);
  };

  const warn = (...args) => {
    if (!config.debug) return;
    // eslint-disable-next-line no-console
    console.warn('[auto-add]', ...args);
  };

  const shouldThrottle = () => Date.now() - lastAutoActionAt < 800;

  const isCartMutationUrl = (url) => {
    if (typeof url !== 'string' || !url) return false;
    return /(?:^|\/)cart\/(?:add|change|update|clear)(?:\.js)?/i.test(url);
  };

  const refreshCartUiOnce = () => {
    try {
      if (window.ajaxCart && typeof window.ajaxCart.load === 'function') {
        window.ajaxCart.load();
      }
    } catch (e) {}

    try {
      if (window.Shopify && typeof window.Shopify.getCart === 'function') {
        window.Shopify.getCart();
      }
    } catch (e) {}
  };

  const getCart = async () => {
    if (window.jQuery) {
      return window.jQuery.getJSON('/cart.js?cb=' + Date.now());
    }
    const response = await fetch('/cart.js?cb=' + Date.now(), { credentials: 'same-origin', cache: 'no-store' });
    return response.json();
  };

  const getVariantIdForHandle = async (handle) => {
    const url = '/products/' + encodeURIComponent(handle) + '.js?cb=' + Date.now();
    const product = window.jQuery
      ? await window.jQuery.getJSON(url)
      : await (await fetch(url, { credentials: 'same-origin', cache: 'no-store' })).json();
      const variants = (product && product.variants) || [];
      const availableVariant = variants.find((v) => v && v.available) || variants[0];
      return availableVariant ? availableVariant.id : null;
  };

  const addVariantToCart = async (variantId, quantity) => {
    if (window.jQuery) {
      return window.jQuery.ajax({
        type: 'POST',
        url: '/cart/add.js',
        data: 'quantity=' + encodeURIComponent(String(quantity)) + '&id=' + encodeURIComponent(String(variantId)),
        dataType: 'json'
      });
    }
    const body = new URLSearchParams();
    body.set('quantity', String(quantity));
    body.set('id', String(variantId));
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8', Accept: 'application/json' },
      body
    });
    return response.json();
  };

  const changeVariantQuantity = async (variantId, quantity) => {
    if (window.jQuery) {
      return window.jQuery.ajax({
        type: 'POST',
        url: '/cart/change.js',
        data: 'quantity=' + encodeURIComponent(String(quantity)) + '&id=' + encodeURIComponent(String(variantId)),
        dataType: 'json'
      });
    }
    const body = new URLSearchParams();
    body.set('quantity', String(quantity));
    body.set('id', String(variantId));
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8', Accept: 'application/json' },
      body
    });
    return response.json();
  };

  const findItemByHandle = (cart, handle) => {
    const items = (cart && cart.items) || [];
    return items.find((i) => i && i.handle === handle) || null;
  };

  const ensureAutoItem = async () => {
    if (isRunning) return;
    if (shouldThrottle()) return;
    if (!config.productHandle) return;

    const qty = Number(config.desiredQuantity);
    if (Number.isNaN(qty) || qty < 1) return;

    isRunning = true;
    try {
      const cart = await getCart();
      const existingItem = findItemByHandle(cart, config.productHandle);

      if (!config.enabled) {
        if (config.removeWhenDisabled && existingItem) {
          lastAutoActionAt = Date.now();
          await changeVariantQuantity(existingItem.id, 0);
          refreshCartUiOnce();
        }
        return;
      }

      if (config.onlyWhenCartHasItems) {
        const cartHasOtherItems =
          cart.item_count > 0 && (!existingItem || cart.item_count > (existingItem.quantity || 0));
        if (!cartHasOtherItems) return;
      }

      if (existingItem) {
        if ((existingItem.quantity || 0) < qty) {
          lastAutoActionAt = Date.now();
          await changeVariantQuantity(existingItem.id, qty);
          refreshCartUiOnce();
        }
        return;
      }

      const variantId = await getVariantIdForHandle(config.productHandle);
      if (!variantId) {
        warn('Could not find variant for handle', config.productHandle);
        return;
      }

      lastAutoActionAt = Date.now();
      await addVariantToCart(variantId, qty);
      refreshCartUiOnce();
      log('auto-added', { variantId, qty });
    } catch (e) {
      warn('auto-add failed', e);
    } finally {
      isRunning = false;
    }
  };

  const init = () => {
    // Run once on page load
    ensureAutoItem();

    // Most reliable: hook into this theme's cart callbacks.
    // cart.api.js calls Shopify.onItemAdded() after successful add.
    // ajax-cart.js calls ShopifyAPI.onItemAdded() / triggers afterAddItem.ajaxCart.
    try {
      if (window.Shopify && typeof window.Shopify.onItemAdded === 'function') {
        const original = window.Shopify.onItemAdded;
        window.Shopify.onItemAdded = function onItemAddedPatched(...args) {
          let result;
          try {
            result = original.apply(this, args);
          } finally {
            window.setTimeout(ensureAutoItem, 150);
          }
          return result;
        };
      }
    } catch (e) {}

    try {
      if (window.ShopifyAPI && typeof window.ShopifyAPI.onItemAdded === 'function') {
        const original = window.ShopifyAPI.onItemAdded;
        window.ShopifyAPI.onItemAdded = function onItemAddedPatched(...args) {
          let result;
          try {
            result = original.apply(this, args);
          } finally {
            window.setTimeout(ensureAutoItem, 150);
          }
          return result;
        };
      }
    } catch (e) {}

    // Also hook cart update callbacks (quantity changes).
    try {
      if (window.Shopify && typeof window.Shopify.onCartUpdate === 'function') {
        const original = window.Shopify.onCartUpdate;
        window.Shopify.onCartUpdate = function onCartUpdatePatched(...args) {
          let result;
          try {
            result = original.apply(this, args);
          } finally {
            window.setTimeout(ensureAutoItem, 150);
          }
          return result;
        };
      }
    } catch (e) {}

    // Primary: Timber ajax cart hooks
    if (window.jQuery) {
      const $body = window.jQuery(document.body);
      $body.on('afterAddItem.ajaxCart afterChangeItem.ajaxCart', () => {
        window.setTimeout(ensureAutoItem, 150);
      });
    }

    // Fallback: any ajax call to cart endpoints
    if (window.jQuery) {
      window.jQuery(document).ajaxComplete((event, xhr, settings) => {
        try {
          const url = (settings && settings.url) || '';
          if (!isCartMutationUrl(url)) return;
          window.setTimeout(ensureAutoItem, 150);
        } catch (e) {}
      });
    }

    // Fallback: fetch/XHR based cart updates
    if (window.fetch) {
      const originalFetch = window.fetch.bind(window);
      window.fetch = (...args) => {
        const input = args[0];
        const url =
          typeof input === 'string'
            ? input
            : input && typeof input.url === 'string'
              ? input.url
              : '';
        return originalFetch(...args).then((response) => {
          try {
            if (isCartMutationUrl(url)) window.setTimeout(ensureAutoItem, 150);
          } catch (e) {}
          return response;
        });
      };
    }

    if (window.XMLHttpRequest) {
      const originalOpen = window.XMLHttpRequest.prototype.open;
      window.XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
        this.__autoAddCartUrl = url;
        return originalOpen.call(this, method, url, ...rest);
      };
      const originalSend = window.XMLHttpRequest.prototype.send;
      window.XMLHttpRequest.prototype.send = function send(body) {
        try {
          this.addEventListener('loadend', () => {
            try {
              if (isCartMutationUrl(this.__autoAddCartUrl)) window.setTimeout(ensureAutoItem, 150);
            } catch (e) {}
          });
        } catch (e) {}
        return originalSend.call(this, body);
      };
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
